// This file will export an array or object that will later hold the configuration
// for each tutorial step (e.g., target element, content, position).

// Helper function to check if an element is visible
const isElementVisible = (selector) => {
  const element = document.querySelector(selector);
  if (!element) return false;
  let currentElement = element;
  while (currentElement && currentElement !== document.body) {
    if (window.getComputedStyle(currentElement).display === 'none') {
      return false;
    }
    currentElement = currentElement.parentElement;
  }
  return element.offsetParent !== null && window.getComputedStyle(element).visibility !== 'hidden';
};

const doesElementExist = (selector) => {
  return !!document.querySelector(selector);
};

const isTimerSettingEnabled = () => {
  try { return localStorage.getItem('showTimer') === 'true'; } catch (e) { return false; }
};
const areImageButtonsEnabled = () => {
  try { return localStorage.getItem('showImageButtons') === 'true'; } catch (e) { return false; }
};

// Standard button types for Shepherd
// 'skip' and 'disable' will be handled by Shepherd event handlers in TutorialContext
// or by custom button actions that directly call context methods if possible via step context.
// For now, we'll use standard Shepherd actions and rely on onComplete/onCancel.

// Note: Button actions are defined as functions that close over the 'tour' instance.
// This is crucial for compatibility with Shepherd.js v11.2.0, which had issues
// with string-based actions (e.g., 'next', 'cancel') in its Svelte components.

export const getFirstStepButtons = (tour) => [
  { text: 'Skip', action: () => tour.cancel(), classes: 'shepherd-button-secondary' },
  { text: 'Next', action: () => tour.next(), classes: 'shepherd-button-primary' }
];

export const getLastStepButtons = (tour) => [
  { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
  { text: 'Done', action: () => tour.complete(), classes: 'shepherd-button-primary' }
];

export const getLastStepDontShowButtons = (tour) => [
  { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
  { text: "Don't Show Again", action: () => tour.complete(), classes: 'shepherd-button-secondary' },
  { text: 'Finish', action: () => tour.complete(), classes: 'shepherd-button-primary' }
];


export const navbarTutorialSteps = [
  {
    id: 'navbar-intro',
    text: 'Welcome to MyDailyOne! This quick tour will show you around the main navigation. You can use the "Skip" button at any time to exit the current tour segment.',
    attachTo: { element: '[data-testid="app-logo"]', on: 'bottom' },
    scrollTo: true,
    buttons: getFirstStepButtons, // Updated
  },
  {
    id: 'logo',
    text: 'This is the MyDailyOne logo. It often brings you back to the main landing page or dashboard.',
    attachTo: { element: '[data-testid="app-logo"]', on: 'bottom' },
    scrollTo: true,
    buttons: (tour) => [{ text: 'Next', action: () => tour.next(), classes: 'shepherd-button-primary' }], // Updated
  },
  // ... other navbar steps
  {
    id: 'portrait-chat-toggle',
    text: 'On portrait screens, use this to show or hide the chat panel.',
    attachTo: { element: 'button[aria-label*="Chat"]', on: 'left' },
    scrollTo: true,
    canShow: () => isElementVisible('button[aria-label*="Chat"]'),
    buttons: getLastStepDontShowButtons, // Updated
  },
];


export const dashboardTutorialSteps = [
  {
    id: 'dashboard-intro',
    text: 'Welcome to the Dashboard! This is where you manage your goals and tasks in a hierarchical tree structure. Use "Skip" to leave this part of the tour.',
    attachTo: { element: 'body', on: 'top' }, // Changed
    scrollTo: true,
    buttons: getFirstStepButtons, // Updated
    // canShow property removed for this test
  },
  // ... other dashboard steps
  {
    id: 'dashboard-layout-chat',
    text: 'The dashboard is resizable. Your chat panel is also available here to interact with your AI coaches.',
    attachTo: { element: 'body', on: 'center' },
    scrollTo: false,
    buttons: getLastStepDontShowButtons, // Updated
  },
];

export const chatboxTutorialSteps = [
  {
    id: 'chatbox-intro',
    text: 'This is the ChatBox where you interact with your AI assistants. Use "Skip" to leave this part of the tour.',
    attachTo: { element: '[data-testid="chatbox-message-input"]', on: 'top' },
    scrollTo: true,
    buttons: getFirstStepButtons, // Updated
    canShow: () => isElementVisible('[data-testid="chatbox-message-input"]'),
  },
  // ... other chatbox steps
  {
    id: 'suggestion-points-find',
    text: "Click the search icon ('Find') here to see the relevant task on your dashboard.",
    attachTo: { element: '[data-testid="suggestion-points-find-button"]', on: 'right' },
    scrollTo: true,
    buttons: getLastStepDontShowButtons, // Updated
    canShow: () => isElementVisible('[data-testid="suggestion-points-find-button"]'),
  },
];

export const todayPageTutorialSteps = [
  {
    id: 'todaypage-intro',
    text: 'Welcome to the Today Page! This page shows your urgent tasks and activities where you have made progress today. Use "Skip" to leave this part of the tour.',
    attachTo: { element: 'body', on: 'top' },
    scrollTo: false,
    buttons: getFirstStepButtons, // Updated
  },
  // ... other today page steps
  {
    id: 'todaypage-first-points-card-intro',
    text: "Interactions here are similar to urgent tasks. Let's quickly review.",
    attachTo: { element: '[data-testid="today-points-activities-title"] ~ div [data-testid="urgent-node-card"]:first-of-type', on: 'bottom' },
    scrollTo: true,
    buttons: getLastStepDontShowButtons, // Updated
    canShow: () => isElementVisible('[data-testid="today-points-activities-title"] ~ div [data-testid="urgent-node-card"]:first-of-type'),
  },
];

export const profilePageTutorialSteps = [
  {
    id: 'profile-intro',
    text: 'This is your Profile page. Here you can update your personal details, manage preferences, and interact with your knowledge graph. Use "Skip" to leave this part of the tour.',
    attachTo: { element: 'body', on: 'top' },
    scrollTo: false,
    buttons: getFirstStepButtons, // Updated
  },
  // ... other profile page steps
  {
    id: 'profile-google-calendar',
    text: 'This button is intended for connecting your Google Calendar. (This feature may be under development).',
    attachTo: { element: '[data-testid="profile-google-calendar-button"]', on: 'top' },
    scrollTo: true,
    buttons: getLastStepDontShowButtons, // Updated
    canShow: () => isElementVisible('[data-testid="profile-google-calendar-button"]'),
  },
];

export const pendingUpdatesPageTutorialSteps = [
  {
    id: 'pending-updates-intro',
    text: 'This page lists profile information updates suggested by your AI assistants. You can review each suggestion and decide whether to accept or reject it. Use "Skip" to leave this part of the tour.',
    attachTo: { element: 'h5', on: 'bottom' },
    scrollTo: true,
    buttons: getFirstStepButtons, // Updated
  },
  // ... other pending updates steps
  {
    id: 'pending-updates-none',
    text: 'There are currently no pending updates for your profile.',
    attachTo: { element: '[data-testid="no-pending-updates-message"]', on: 'bottom' },
    scrollTo: true,
    buttons: getLastStepDontShowButtons, // Updated
    canShow: () => isElementVisible('[data-testid="no-pending-updates-message"]'),
  },
];

export const agentsPageTutorialSteps = [
  {
    id: 'agents-intro',
    text: 'This page lists all your available AI coaching agents. You can search for specific agents and customize their behavior. Use "Skip" to leave this part of the tour.',
    attachTo: { element: '[data-testid="agents-page-title"]', on: 'bottom' },
    scrollTo: true,
    buttons: getFirstStepButtons, // Updated
    canShow: () => isElementVisible('[data-testid="agents-page-title"]'),
  },
  // ... other agents page steps (ensure intermediate steps use appropriate buttons like [{ text: 'Next', action: 'next'}])
  { // Example intermediate step
    id: 'agents-search',
    text: 'Use this search bar to quickly find an agent by name or description.',
    attachTo: { element: '[data-testid="agents-search-input"]', on: 'bottom' },
    scrollTo: true,
    buttons: (tour) => [{text: 'Next', action: () => tour.next(), classes: 'shepherd-button-primary'}], // Updated
    canShow: () => isElementVisible('[data-testid="agents-search-input"]'),
  },
  {
    id: 'edit-agent-cancel', // This is within the dialog
    text: 'Or click "Cancel" to discard any changes you\'ve made in this dialog.',
    attachTo: { element: '[data-testid="edit-agent-cancel-button"]', on: 'right' },
    scrollTo: true,
    buttons: (tour) => [ // Updated to pass tour
      { text: 'Back', action: () => tour.back(), classes: 'shepherd-button-secondary' },
      {
        text: 'Finish Customization',
        action: () => { // Changed to arrow function, uses tour instance
          const cancelButton = document.querySelector('[data-testid="edit-agent-cancel-button"]');
          if (cancelButton) cancelButton.click(); // Close the dialog
          tour.next(); // Advance to next step if any, or complete if this is the last sub-step
        },
        classes: 'shepherd-button-primary'
      }
    ],
    canShow: () => isElementVisible('[data-testid="edit-agent-cancel-button"]'),
  },
   {
    id: 'agents-no-agents',
    text: 'If you don\'t see any agents, it might be because none match your search, or your setup is still in progress.',
    attachTo: { element: '[data-testid="agents-no-agents-message"]', on: 'bottom' },
    scrollTo: true,
    buttons: getLastStepDontShowButtons, // Updated
    canShow: () => isElementVisible('[data-testid="agents-no-agents-message"]'),
  },
];

// Default export for other potential tutorial step arrays
const tutorialSteps = [];
export default tutorialSteps;
